import {
  capitalizeFirstLetter,
  careEnvironmentDefs,
  carePhaseDefs,
  formatCurrency,
  formatPercent,
  CareEnvironment,
  pickClientCareEnvironmentCosts,
  isHealthcareInflationRateDefault,
} from '@shared';
import CareEnvironmentCostModal from './CareEnvironmentCostModal';
import React, { useState } from 'react';
import { EditIconButton } from './EditIconButton';
import EditHealthCareInflationModal from './EditHealthCareInflationModal';

interface CarePhaseCostSummaryProps {
  client: Client;
  carePhase: CarePhase;
}

export function CarePhaseCostSummary({ client, carePhase }: CarePhaseCostSummaryProps) {
  const {
    intakeSurvey: { clientLtcZipCode, clientCurrentCarePhase },
    phaseCosts,
    clientYearsTillPhaseEnd,
    clientPhaseInflationFactors,
    clientCalculationSettings: { healthCareInflationRate },
  } = client;

  const [openEditCostModal, setOpenEditCostModal] = useState(false);
  const [openEditInflationModal, setOpenEditInflationModal] = useState(false);
  const carePhaseIndex = carePhaseDefs[carePhase].index;
  const selectedCarePhaseCosts = phaseCosts[carePhaseIndex];

  // phase is in the past
  if (clientCurrentCarePhase && carePhaseIndex < carePhaseDefs[clientCurrentCarePhase].index) {
    return null;
  }

  const yearsTillPhaseEnd = clientYearsTillPhaseEnd[carePhase];
  const phaseInflationFactor = clientPhaseInflationFactors[carePhase];
  const selectedCareSetting = client.careEnvironmentSelections?.[carePhase];
  const isCareSettingHomeCare = selectedCareSetting === CareEnvironment.home;
  const clientCareEnvironmentCostData = selectedCareSetting
    ? pickClientCareEnvironmentCosts(client, selectedCareSetting)
    : undefined;

  const isDurationCustom = selectedCarePhaseCosts.isDurationCustom;
  const rateLocalDescription = clientCareEnvironmentCostData?.rateLocaleDescription;

  return (
    <div className="border-primary-200 bg-primary-100 h-4/5 rounded-lg border-[1.5px] border-dashed px-4 py-6 lg:flex-grow lg:px-6">
      <h2
        id="summary-heading"
        className="text-base font-medium text-gray-800 md:text-lg"
      >
        Cost Summary ({carePhaseDefs[carePhase].label})
      </h2>

      <dl className="mt-4 space-y-3 md:mt-6">
        <div className="flex items-center justify-between">
          <dt className="text-sm text-gray-800 md:text-base">
            {selectedCareSetting
              ? isCareSettingHomeCare
                ? 'Home care'
                : capitalizeFirstLetter(careEnvironmentDefs[selectedCareSetting].label)
              : 'Care setting'}{' '}
            cost
            <div className="text-sm text-gray-500">{rateLocalDescription}</div>
          </dt>
          <dd
            className={`${selectedCareSetting ? '' : 'hidden'} flex items-center text-sm font-medium text-gray-800 md:text-base`}
          >
            <EditIconButton
              onClick={() => setOpenEditCostModal(true)}
              tooltipText="Edit cost"
            />

            {formatCurrency(clientCareEnvironmentCostData?.rateAmount) +
              '/' +
              (isCareSettingHomeCare ? 'hr' : 'mo')}
          </dd>
          <dd
            className={`${selectedCareSetting ? 'hidden' : ''} flex items-center text-base font-medium text-gray-800`}
          >
            $ --
          </dd>
        </div>
        <div className="border-primary-200 flex items-center justify-between border-t pt-4">
          <dt className="text-sm text-gray-800 md:text-base">
            <span>
              {isCareSettingHomeCare
                ? 'Professional care hours'
                : isDurationCustom
                  ? 'Custom duration'
                  : 'Estimated duration'}
            </span>
          </dt>
          <dd className="text-sm font-medium text-gray-800 md:text-base">
            {isCareSettingHomeCare
              ? Math.round(selectedCarePhaseCosts.phaseProfessionalCareHoursProvided) + ' hrs'
              : Math.round(selectedCarePhaseCosts.phaseCareMonthsNeeded) + ' months'}
          </dd>
        </div>
        <div className="border-primary-200 flex items-center justify-between border-t pt-4">
          <dt className="text-sm text-gray-800 md:text-base">
            Healthcare inflation
            <div className="text-sm text-gray-500">
              Over{' '}
              {yearsTillPhaseEnd! < 1
                ? `${Math.round(selectedCarePhaseCosts.phaseCareMonthsNeeded)} months`
                : `${yearsTillPhaseEnd!.toFixed(1)} year${yearsTillPhaseEnd === 1 ? '' : 's'}`}{' '}
              {!isHealthcareInflationRateDefault(healthCareInflationRate) ? '(edited)' : ''}
            </div>
          </dt>

          <dd className="text-sm font-medium text-gray-800 md:text-base">
            <EditIconButton
              onClick={() => setOpenEditInflationModal(true)}
              tooltipText="Edit inflation rate"
            />
            {formatPercent(phaseInflationFactor, 1)}
          </dd>
        </div>
        <div className="border-primary-200 flex items-center justify-between border-t pt-4">
          <dt className="text-sm font-medium text-gray-800 md:text-base">Total</dt>
          <dd className="text-sm font-semibold text-gray-800 md:text-base">
            {selectedCareSetting
              ? formatCurrency(selectedCarePhaseCosts.phaseInflatedProfessionalShareCost)
              : '$ --'}
          </dd>
        </div>
      </dl>
      {selectedCareSetting && (
        <CareEnvironmentCostModal
          selectedCareEnvironment={selectedCareSetting}
          open={openEditCostModal}
          setOpen={setOpenEditCostModal}
        />
      )}
      <EditHealthCareInflationModal
        open={openEditInflationModal}
        setOpen={setOpenEditInflationModal}
      />
    </div>
  );
}
